import type { FollowupSelectionFamily } from "@repo/shared/types";
import { Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./client-mode-summary.i18n";
import { useReasonText } from "./use-reason-text";

type ClientModeSummaryProps = {
  families: FollowupSelectionFamily[];
  /** QL-375 group 8 — show "Carries mandate {name}" on account rows; off when the flow's collection has mandates off. */
  showMandates: boolean;
};

/**
 * Read-only listing for the "follow up entire client" mode: every eligible
 * agreement and account is included (one successor goal per family, DP-1);
 * ineligible items render greyed-out with the server reason (DP-2) so the
 * advisor sees exactly what is left out.
 */
export function ClientModeSummary({ families, showMandates }: ClientModeSummaryProps) {
  const t = useT(T);
  const reasonText = useReasonText();
  return (
    <div className="space-y-3">
      {families.map((family) => (
        <div
          key={family.goalIds[0] ?? `${family.items[0]?.type}:${family.items[0]?.id}`}
          className="border rounded-lg"
        >
          <div className="px-3 py-2 border-b bg-muted/40">
            <span className="font-medium text-sm min-w-0 truncate">
              {family.name ?? t.unlinkedGroup}
            </span>
          </div>
          <div className="divide-y">
            {family.items.map((row) => (
              <div
                key={`${row.type}:${row.id}`}
                className={cn(
                  "flex flex-wrap items-center gap-2 px-3 py-2 text-sm",
                  !row.eligible && "opacity-60",
                )}
              >
                {row.eligible ? (
                  <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                ) : (
                  <span className="h-4 w-4 shrink-0" aria-hidden />
                )}
                <span className="min-w-0 truncate">{row.name}</span>
                <Badge variant="outline" className="shrink-0">
                  {row.type === "agreement" ? t.agreement : (row.wrapperType ?? t.account)}
                </Badge>
                {!row.eligible && (
                  <span className="ml-auto shrink-0 text-muted-foreground text-xs text-right">
                    {reasonText(row.reason ?? "")}
                  </span>
                )}
                {row.eligible && row.droppedIsins && row.droppedIsins.length > 0 && (
                  <span className="w-full basis-full pl-6 text-amber-600 text-xs">
                    {`${t.droppedFundsWarning} ${row.droppedIsins.join(", ")}`}
                  </span>
                )}
                {showMandates && row.type === "account" && row.mandateName && (
                  <span className="w-full basis-full pl-6 text-muted-foreground text-xs">
                    {t.carriesMandate(row.mandateName)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
